import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';
import theme from '../../styles/theme';
import TripMap from './TripMap';
import { useAuth } from '../../contexts/AuthContext';
import styled from 'styled-components';
import {
  convertSpeed as utilConvertSpeed,
  getSpeedUnit as utilGetSpeedUnit,
  convertDistance as utilConvertDistance,
  getDistanceUnit as utilGetDistanceUnit,
  convertTemperature as utilConvertTemperature,
  getTemperatureUnit as utilGetTemperatureUnit,
  getFuelUnit as utilGetFuelUnit,
  fuelRateToLitersPerHour,
  formatCombinedFuelRate,
  formatFuelRate,
  resolveTripPreferenceUnits,
} from '../../utils/unitConversions';
import { resolveAlternateGpsTripId, routeHasGpsData, resolveEffectiveTripTrack } from '../../utils/tripGpsResolve';
import { resolveTripDisplayStats } from '../../utils/tripTrackStats';

/**
 * Trip Map Visualization Component
 * 
 * Displays trip GPS track on Google Maps with engine telemetry overlay
 * Shows RPM, temperature, fuel rate, and speed at each track point
 */

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: ${theme.colors.background.primary};
  padding: 1.5rem;
`;

const HeaderCard = styled.div`
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid ${theme.colors.border.primary};
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const StatsGrid = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const StatLabel = styled.span`
  color: ${theme.colors.text.secondary};
  font-size: 0.875rem;
`;

const StatValue = styled.strong`
  color: ${theme.colors.text.primary};
  font-size: 1rem;
`;

const ControlsCard = styled.div`
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.lg};
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid ${theme.colors.border.primary};
`;

const ControlsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const ControlsLabel = styled.label`
  font-weight: 500;
  color: ${theme.colors.text.primary};
`;

const ConfigButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.primary};
  background: ${props => props.active ? theme.colors.primary.main : theme.colors.background.secondary};
  color: ${props => props.active ? 'white' : theme.colors.text.primary};
  font-weight: 500;
  cursor: pointer;
  transition: ${theme.transitions.default};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TelemetryButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const TelemetryButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: ${theme.borderRadius.md};
  border: 2px solid ${props => props.active ? theme.colors.primary.main : theme.colors.border.primary};
  background: ${props => props.active ? theme.colors.primary.main : theme.colors.background.secondary};
  color: ${props => props.active ? 'white' : theme.colors.text.primary};
  font-weight: ${props => props.active ? '600' : '500'};
  cursor: pointer;
  transition: ${theme.transitions.default};
`;

const ConfigPanel = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.primary};
`;

const ConfigGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const ConfigItem = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: ${theme.borderRadius.sm};
  transition: background 0.2s ease;

  &:hover {
    background: ${theme.colors.background.primary};
  }
`;

const ConfigCheckbox = styled.input`
  margin-top: 0.25rem;
  cursor: pointer;
`;

const ConfigText = styled.div`
  flex: 1;
`;

const ConfigTitle = styled.div`
  font-weight: 500;
  font-size: 0.875rem;
  color: ${theme.colors.text.primary};
`;

const ConfigDescription = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
  margin-top: 0.125rem;
`;

const MapContainer = styled.div`
  margin-bottom: 1.5rem;
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  border: 1px solid ${theme.colors.border.primary};
`;

const TableCard = styled.div`
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
`;

const TableHeader = styled.div`
  background: ${theme.colors.background.primary};
  padding: 1rem;
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const TableTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const TableContainer = styled.div`
  max-height: 400px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
`;

const TableHead = styled.thead`
  /* Sticky lives on th cells — border-collapse:collapse breaks thead sticky in Chrome/Safari */
`;

const TableHeaderCell = styled.th`
  position: sticky;
  top: 0;
  z-index: 3;
  padding: 0.75rem;
  text-align: left;
  border-bottom: 2px solid ${theme.colors.border.primary};
  font-weight: 600;
  color: ${theme.colors.text.primary};
  background: ${theme.colors.background.primary};
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.12);
  white-space: nowrap;
`;

const TableRow = styled.tr`
  background: ${props => props.selected ? theme.colors.primary.light : (props.even ? theme.colors.background.secondary : theme.colors.background.paper)};
  cursor: pointer;
  border-left: 4px solid ${props => props.color || 'transparent'};
  transition: background 0.2s ease;

  &:hover {
    background: ${props => props.selected ? theme.colors.primary.light : theme.colors.background.primary};
  }
`;

const InfoBanner = styled.div`
  background: rgba(59, 130, 246, 0.12);
  border: 1px solid rgba(59, 130, 246, 0.35);
  border-radius: ${theme.borderRadius.md};
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  color: ${theme.colors.text.secondary};
  font-size: 0.9rem;
  line-height: 1.45;
`;

const TableCell = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.primary};
  font-size: ${props => props.small ? '0.875rem' : '1rem'};
`;

const TripMapVisualization = ({ tripId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [waypoints, setWaypoints] = useState([]);
  const [trackPoints, setTrackPoints] = useState([]);
  const [mapTrack, setMapTrack] = useState([]);
  const [routeMeta, setRouteMeta] = useState({ track_count: 0, telemetry_sampled: false });
  const [telemetryLoading, setTelemetryLoading] = useState(false);
  const telemetryLoadRef = useRef(null);
  const [routeTimes, setRouteTimes] = useState({ start: null, end: null });
  const [loadError, setLoadError] = useState(null);
  const [gpsSiblingTripId, setGpsSiblingTripId] = useState(null);
  const [consolidating, setConsolidating] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [selectedWaypointIndex, setSelectedWaypointIndex] = useState(null);
  const rowRefs = useRef({});
  const [telemetryView, setTelemetryView] = useState('speed'); // speed, rpm, temp, fuel
  const [showConfig, setShowConfig] = useState(false);
  const [userPreferences, setUserPreferences] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({
    time: true,
    position: true,
    speed: true,
    rpm: true,
    temp: true,
    load: true,
    fuelRate: true,
    heading: true,
    depth: true,
  });

  useEffect(() => {
    if (tripId) {
      fetchTripData();
      fetchUserPreferences();
      // Load saved telemetry column configuration from server on mount
      loadColumnConfiguration();
    }
  }, [tripId]);

  const fetchUserPreferences = async () => {
    try {
      const response = await api.get('/api/trip-preferences');
      const prefs = response.data.preferences || response.data;
      setUserPreferences(prefs);
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      // Set defaults if fetch fails
      setUserPreferences({
        speed_unit: 'knots',
        distance_unit: 'nautical_miles',
        temperature_units: 'celsius',
        fuel_units: 'liters'
      });
    }
  };

  // Unit conversion functions
  const toKnotsFromMps = (mps) => (typeof mps === 'number' ? (mps * 1.943844) : 0);
  const prefUnits = resolveTripPreferenceUnits(userPreferences || {});

  const displaySpeed = (baseKnots) => utilConvertSpeed(baseKnots || 0, prefUnits.speed_unit);
  const displaySpeedUnit = () => utilGetSpeedUnit(prefUnits.speed_unit);

  const displayDistance = (nauticalMiles) => utilConvertDistance(nauticalMiles || 0, prefUnits.distance_unit);
  const displayDistanceUnit = () => utilGetDistanceUnit(prefUnits.distance_unit);

  const displayTemperature = (celsius) => utilConvertTemperature(celsius || 0, prefUnits.temperature_unit);
  const displayTemperatureUnit = () => utilGetTemperatureUnit(prefUnits.temperature_unit);

  const displayFuelRateUnit = () => `${utilGetFuelUnit(prefUnits.fuel_unit)}/hr`;

  // Column configuration saving
  const saveColumnConfiguration = async () => {
    try {
      const res = await api.post('/api/user-preferences/columns', {
        trip_map_columns: visibleColumns
      });
      const saved = res?.data?.trip_map_columns;
      if (saved && typeof saved === 'object') {
        setVisibleColumns(saved);
      }
      toast.success('Column configuration saved');
    } catch (error) {
      console.error('Error saving column configuration:', error);
      toast.error('Failed to save column configuration');
    }
  };

  const loadColumnConfiguration = async () => {
    try {
      const response = await api.get('/api/user-preferences/columns');
      const cols = response?.data?.trip_map_columns;
      if (cols && typeof cols === 'object') setVisibleColumns(cols);
    } catch (error) {
      console.error('Error loading column configuration:', error);
    }
  };

  const routeTripIdForTelemetry = (route, fallbackId) => {
    const from = parseInt(route?.resolved_from_trip_id, 10);
    if (from > 0) return from;
    return fallbackId;
  };

  const loadTelemetryInBackground = async (telemetryTripId) => {
    if (!telemetryTripId || telemetryLoadRef.current === telemetryTripId) return;
    telemetryLoadRef.current = telemetryTripId;
    setTelemetryLoading(true);
    try {
      const response = await api.get(
        `/api/trips/${telemetryTripId}/route?telemetry=1&stats=0`,
        { timeout: 90000 }
      );
      const route = response.data || {};
      const points = route.points || [];
      if (points.length === 0) return;

      setTrackPoints(points);
      setRouteMeta((prev) => ({
        ...prev,
        telemetry_sampled: Boolean(route.telemetry_sampled),
        telemetry_engine_preferred: Boolean(route.telemetry_engine_preferred),
        telemetry_engine_row_count: route.telemetry_engine_row_count,
        telemetry_full: Boolean(route.telemetry_full),
        track_count: route.track_count || prev.track_count,
      }));
      if (route.start?.time || route.end?.time) {
        setRouteTimes((prev) => ({
          start: route.start?.time || prev.start,
          end: route.end?.time || prev.end,
        }));
      }

      setAnalytics((prev) => {
        const nextStats = resolveTripDisplayStats({
          trip: prev?.trip || trip,
          routeStats: route.stats || (prev?.stats
            ? {
                distanceKm: prev.stats.distance?.km || 0,
                distanceNm: prev.stats.distance?.nm || 0,
                durationMinutes: prev.stats.duration?.minutes || 0,
                avgSpeedKnots: prev.stats.speed?.average || 0,
                maxSpeedKnots: prev.stats.speed?.max || 0,
              }
            : null),
          track: mapTrack,
          points,
          waypoints,
          routeStartTime: route.start?.time || routeTimes.start,
          routeEndTime: route.end?.time || routeTimes.end,
        });
        return {
          ...prev,
          stats: nextStats
            ? {
                distance: { km: nextStats.distanceKm || 0, nm: nextStats.distanceNm || 0 },
                duration: {
                  minutes: nextStats.durationMinutes || 0,
                  hours: nextStats.durationMinutes ? (nextStats.durationMinutes / 60).toFixed(2) : 0,
                },
                speed: {
                  average: nextStats.avgSpeedKnots || 0,
                  max: nextStats.maxSpeedKnots || 0,
                },
                fuel: {
                  total: parseFloat(prev?.trip?.total_fuel_used_liters || trip?.total_fuel_used_liters || 0) || 0,
                  efficiency: 0,
                },
              }
            : prev?.stats,
        };
      });
    } catch (err) {
      console.warn('Background telemetry load failed:', err?.message || err);
      telemetryLoadRef.current = null;
    } finally {
      setTelemetryLoading(false);
    }
  };

  const fetchTripData = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const fetchRoute = () => api.get(`/api/trips/${tripId}/route?telemetry=0&stats=0`, { timeout: 60000 });

      const tripResult = await Promise.allSettled([
        api.get(`/api/trips/${tripId}?include_waypoints=true`, { timeout: 20000 }),
      ]).then((r) => r[0]);

      let routeResult = await Promise.allSettled([fetchRoute()]).then((r) => r[0]);
      if (routeResult.status === 'rejected') {
        console.warn('Route fetch failed, retrying once:', routeResult.reason);
        routeResult = await Promise.allSettled([fetchRoute()]).then((r) => r[0]);
      }

      const tripPayload = tripResult.status === 'fulfilled' ? tripResult.value.data : null;
      const tripData = tripPayload?.trip || null;
      const route = routeResult.status === 'fulfilled' ? (routeResult.value.data || {}) : {};
      const tripWaypoints = tripPayload?.waypoints || [];

      if (tripResult.status === 'rejected') {
        console.error('Trip fetch failed:', tripResult.reason);
      }
      if (routeResult.status === 'rejected') {
        console.error('Route fetch failed:', routeResult.reason);
      }

      const alternateId = resolveAlternateGpsTripId(tripId, tripPayload, route);
      if (alternateId) {
        toast.info('Your GPS track is on another trip record — opening it now.');
        navigate(`/trips/${alternateId}/map`, { replace: true });
        return;
      }

      const points = route.points || [];
      const { track: polyline, source: trackSource } = resolveEffectiveTripTrack({
        gpsTrack: route.track || [],
        waypoints: tripWaypoints,
      });

      const stats = resolveTripDisplayStats({
        trip: tripData,
        routeStats: route.stats,
        track: polyline,
        points,
        waypoints: tripWaypoints,
        routeStartTime: route.start?.time,
        routeEndTime: route.end?.time,
      });

      const hasGps = polyline.length > 1
        || routeHasGpsData(route)
        || trackSource === 'waypoints';

      setTrip(tripData);
      setWaypoints(tripWaypoints);
      setTrackPoints(points);
      setMapTrack(polyline);
      setGpsSiblingTripId(tripPayload?.gps_sibling_trip_id || null);
      setRouteMeta({
        track_count: route.track_count || polyline.length || points.length,
        telemetry_sampled: Boolean(route.telemetry_sampled),
        telemetry_engine_preferred: Boolean(route.telemetry_engine_preferred),
        telemetry_engine_row_count: route.telemetry_engine_row_count,
        telemetry_full: Boolean(route.telemetry_full),
        route_source: route.route_source || trackSource,
      });
      setRouteTimes({
        start: route.start?.time || tripData?.start_time || null,
        end: route.end?.time || tripData?.end_time || null,
      });
      setAnalytics({
        trip: tripData,
        stats: stats
          ? {
              distance: { km: stats.distanceKm || 0, nm: stats.distanceNm || 0 },
              duration: {
                minutes: stats.durationMinutes || 0,
                hours: stats.durationMinutes ? (stats.durationMinutes / 60).toFixed(2) : 0,
              },
              speed: {
                average: stats.avgSpeedKnots || 0,
                max: stats.maxSpeedKnots || 0,
              },
              fuel: { total: parseFloat(tripData?.total_fuel_used_liters || 0) || 0, efficiency: 0 },
            }
          : null,
      });

      // Fast map may return GPS-only rows; enrich with engine telemetry in background.
      telemetryLoadRef.current = null;
      if (
        hasGps
        && route.track_count > 0
        && route.route_source !== 'waypoints'
        && !route.telemetry_full
      ) {
        loadTelemetryInBackground(routeTripIdForTelemetry(route, tripId));
      }

      if (!tripData) {
        const err = tripResult.status === 'rejected' ? tripResult.reason : null;
        const status = err?.response?.status;
        const msg = err?.response?.data?.error || err?.response?.data?.detail;
        if (status === 404) {
          setLoadError('This trip was not found. It may have been deleted, or the link may be outdated.');
        } else if (status === 403) {
          setLoadError('You do not have access to this trip.');
        } else {
          setLoadError(msg || 'The server timed out loading this trip. Your GPS data is still stored — try again in a moment.');
        }
        toast.error(msg || 'Failed to load trip');
      } else if (!hasGps && routeResult.status === 'rejected' && tripWaypoints.length < 2) {
        setLoadError('GPS route timed out loading. Open Trip Details to merge duplicate records or try again.');
      } else if (!hasGps) {
        setLoadError('No GPS track on this trip record. Your data may be on a duplicate trip or still syncing.');
      } else {
        setLoadError(null);
      }
    } catch (error) {
      console.error('Error fetching trip data:', error);
      toast.error('Failed to load trip visualization');
    } finally {
      setLoading(false);
    }
  };

  const handleConsolidateDuplicates = async () => {
    try {
      setConsolidating(true);
      const res = await api.post(`/api/trips/${tripId}/consolidate-duplicates`, {
        source_trip_id: gpsSiblingTripId || undefined,
      });
      const moved = (res.data?.movedTracks || 0) + (res.data?.movedLegacy || 0);
      const synthesized = res.data?.synthesized?.inserted || 0;
      const msg = res.data?.message;

      if (moved > 0) {
        toast.success(msg || `Merged ${moved} GPS points into this trip`);
      } else if (synthesized > 0) {
        toast.success(msg || `Built GPS track from ${synthesized} anchor stops`);
      } else if (res.data?.hasGpsTrack) {
        toast.info(msg || 'GPS is already on this trip — refreshing…');
      } else {
        toast.warning(msg || 'No GPS found to merge. Try "Repair trip from stops".');
      }
      fetchTripData();
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to merge duplicate trips');
    } finally {
      setConsolidating(false);
    }
  };

  const formatPair = (a, b, fmt = (v) => String(v)) => {
    const left = a != null && Number.isFinite(Number(a)) ? fmt(Number(a)) : '—';
    const right = b != null && Number.isFinite(Number(b)) ? fmt(Number(b)) : '—';
    return `${left}/${right}`;
  };

  const formatFuelLph = (point) =>
    formatCombinedFuelRate(point.fuel_rate_port, point.fuel_rate_starboard, prefUnits.fuel_unit, 2);

  const getTelemetryColor = useCallback((point, view) => {
    if (!point) return '#999';

    switch (view) {
      case 'speed':
        const speed = parseFloat(point.speed_knots);
        if (speed > 5) return '#ef4444'; // Red for fast
        if (speed > 3) return '#f59e0b'; // Orange for medium
        return '#10b981'; // Green for slow

      case 'rpm': {
        const p = parseInt(point.rpm_port, 10);
        const s = parseInt(point.rpm_starboard, 10);
        const avgRpm = [p, s].filter(Number.isFinite).reduce((a, b, _, arr) => a + b / arr.length, 0);
        if (!avgRpm) return '#94a3b8';
        if (avgRpm > 2000) return '#ef4444';
        if (avgRpm > 1000) return '#f59e0b';
        return '#10b981';
      }

      case 'temp': {
        const p = parseFloat(point.temp_port);
        const s = parseFloat(point.temp_starboard);
        const vals = [p, s].filter(Number.isFinite);
        const avgTemp = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
        if (!avgTemp) return '#94a3b8';
        if (avgTemp > 180) return '#ef4444';
        if (avgTemp > 160) return '#f59e0b';
        return '#10b981';
      }

      case 'fuel': {
        const fuelRate =
          fuelRateToLitersPerHour(point.fuel_rate_port) +
          fuelRateToLitersPerHour(point.fuel_rate_starboard);
        if (!fuelRate) return '#94a3b8';
        if (fuelRate > 15) return '#ef4444';
        if (fuelRate > 8) return '#f59e0b';
        return '#10b981';
      }

      default:
        return theme.colors.primary.main;
    }
  }, []);

  const handlePointSelect = useCallback((index) => {
    setSelectedPoint(index);
    const row = rowRefs.current[index];
    if (row?.scrollIntoView) {
      row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, []);

  const handleWaypointSelect = useCallback((index) => {
    setSelectedWaypointIndex(index);
  }, []);

  const formatTelemetry = (point, metric) => {
    if (!point) return 'N/A';

    switch (metric) {
      case 'speed':
        return `${parseFloat(point.speed_knots).toFixed(1)} knots`;
      case 'rpm':
        return `Port: ${point.rpm_port || 0} / Starboard: ${point.rpm_starboard || 0}`;
      case 'temp':
        return `Port: ${displayTemperature(parseFloat(point.temp_port)).toFixed(0)}${displayTemperatureUnit()} / Starboard: ${displayTemperature(parseFloat(point.temp_starboard)).toFixed(0)}${displayTemperatureUnit()}`;
      case 'fuel': {
        const portLph = fuelRateToLitersPerHour(point.fuel_rate_port);
        const stbdLph = fuelRateToLitersPerHour(point.fuel_rate_starboard);
        return `Port: ${portLph ? formatFuelRate(portLph, prefUnits.fuel_unit, 2) : '—'} / Starboard: ${stbdLph ? formatFuelRate(stbdLph, prefUnits.fuel_unit, 2) : '—'}`;
      }
      case 'load':
        return `Port: ${parseFloat(point.load_port).toFixed(1)}% / Starboard: ${parseFloat(point.load_starboard).toFixed(1)}%`;
      default:
        return 'N/A';
    }
  };


  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.125rem', color: theme.colors.text.secondary }}>
          Loading trip visualization...
        </div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.125rem', color: theme.colors.text.secondary }}>
          {loadError || 'Trip not found or you do not have access.'}
        </div>
      </div>
    );
  }

  const hasGpsDisplay = mapTrack.length > 1 || routeHasGpsData({
    track: mapTrack,
    track_count: routeMeta.track_count,
    points: trackPoints,
    route_source: routeMeta.route_source,
  });

  if (!hasGpsDisplay) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>
          {trip.trip_name}
        </div>
        <div style={{ fontSize: '1.125rem', color: theme.colors.text.secondary }}>
          {loadError || 'No GPS track recorded for this trip yet.'}
        </div>
        <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: theme.colors.text.secondary }}>
          If this trip should have a route, merge duplicate records or open Trip Details.
        </div>
        <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          {gpsSiblingTripId && (
            <button
              type="button"
              onClick={() => navigate(`/trips/${gpsSiblingTripId}/map`, { replace: true })}
              style={{
                padding: '0.6rem 1rem',
                borderRadius: theme.borderRadius?.md || 8,
                border: 'none',
                background: theme.colors.primary?.main || '#2563eb',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Open trip with GPS
            </button>
          )}
          <button
            type="button"
            onClick={handleConsolidateDuplicates}
            disabled={consolidating}
            style={{
              padding: '0.6rem 1rem',
              borderRadius: theme.borderRadius?.md || 8,
              border: `1px solid ${theme.colors.border?.primary || '#ccc'}`,
              background: theme.colors.background?.secondary || '#f3f4f6',
              cursor: consolidating ? 'wait' : 'pointer',
            }}
          >
            {consolidating ? 'Merging…' : 'Merge GPS into this trip'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/trips/${tripId}`)}
            style={{
              padding: '0.6rem 1rem',
              borderRadius: theme.borderRadius?.md || 8,
              border: `1px solid ${theme.colors.border?.primary || '#ccc'}`,
              background: 'transparent',
              cursor: 'pointer',
            }}
          >
            Trip Details
          </button>
        </div>
      </div>
    );
  }

  const liveStats = resolveTripDisplayStats({
    trip,
    routeStats: analytics?.stats
      ? {
          distanceKm: analytics.stats.distance?.km || 0,
          distanceNm: analytics.stats.distance?.nm || 0,
          durationMinutes: analytics.stats.duration?.minutes || 0,
          avgSpeedKnots: analytics.stats.speed?.average || 0,
          maxSpeedKnots: analytics.stats.speed?.max || 0,
        }
      : null,
    track: mapTrack,
    points: trackPoints,
    waypoints,
    routeStartTime: routeTimes.start || trip?.start_time,
    routeEndTime: routeTimes.end || trip?.end_time,
  });

  const statsDistance = liveStats.distanceNm || 0;
  const statsDuration = liveStats.durationMinutes || 0;
  let statsAvgSpeed = liveStats.avgSpeedKnots || 0;
  if (statsAvgSpeed <= 0 && statsDuration > 0 && statsDistance > 0) {
    statsAvgSpeed = statsDistance / (statsDuration / 60);
  }
  const statsFuel = (parseFloat(trip?.total_fuel_used_liters || 0) > 0)
    ? parseFloat(trip.total_fuel_used_liters)
    : (parseFloat(liveStats?.fuelUsed || 0) || 0);

  return (
    <Container>
      {/* Trip Header */}
      <HeaderCard>
        <Title>{trip.trip_name}</Title>
        <StatsGrid>
          <StatItem>
            <StatLabel>Distance:</StatLabel>
            <StatValue>{displayDistance(statsDistance).toFixed(2)} {displayDistanceUnit()}</StatValue>
          </StatItem>
          <StatItem>
            <StatLabel>Duration:</StatLabel>
            <StatValue>{statsDuration} min</StatValue>
          </StatItem>
          <StatItem>
            <StatLabel>Avg Speed:</StatLabel>
            <StatValue>{displaySpeed(statsAvgSpeed).toFixed(1)} {displaySpeedUnit()}</StatValue>
          </StatItem>
          <StatItem>
            <StatLabel>Fuel Used:</StatLabel>
            <StatValue>{statsFuel.toFixed(2)} L</StatValue>
          </StatItem>
        </StatsGrid>
      </HeaderCard>

      <InfoBanner>
        <strong>
          {routeMeta.route_source === 'waypoints'
            ? `${waypoints.length} anchor stops`
            : `${routeMeta.track_count || trackPoints.length} GPS points`}
        </strong> on this trip
        {routeMeta.telemetry_engine_preferred
          ? ` (table prefers ${trackPoints.length} underway/engine rows${routeMeta.telemetry_engine_row_count != null ? ` from ${routeMeta.telemetry_engine_row_count} propulsion samples` : ''})`
          : routeMeta.telemetry_sampled
            ? ` (table shows ${trackPoints.length} sampled rows)`
            : ''}
        {routeMeta.route_source === 'waypoints' ? ' — route drawn from stops (GPS track still loading or on a duplicate record)' : ''}
      </InfoBanner>

      {/* Telemetry View Selector and Config Button */}
      <ControlsCard>
        <ControlsHeader>
          <ControlsLabel>Telemetry Overlay:</ControlsLabel>
          <ConfigButton
            active={showConfig}
            onClick={() => setShowConfig(!showConfig)}
          >
            ⚙️ Configure Columns
          </ConfigButton>
        </ControlsHeader>
        
        {/* Configuration Panel */}
        {showConfig && (
          <ConfigPanel>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem', color: theme.colors.text.primary }}>
              Select Data Points to Display:
            </h4>
            <ConfigGrid>
              {[
                { key: 'time', label: '🕐 Time', description: 'Timestamp of reading' },
                { key: 'position', label: '📍 GPS Position', description: 'Lat/Lon coordinates' },
                { key: 'speed', label: '⚡ Speed', description: 'Speed over ground' },
                { key: 'rpm', label: '🔧 Engine RPM', description: 'Port & Starboard RPM' },
                { key: 'temp', label: '🌡️ Temperature', description: 'Engine temperatures' },
                { key: 'load', label: '📊 Engine Load', description: 'Engine load percentage' },
                { key: 'fuelRate', label: '⛽ Fuel Rate', description: 'Instantaneous fuel consumption' },
                { key: 'heading', label: '🧭 Heading', description: 'Course over ground' }
              ].map(col => (
                <ConfigItem key={col.key}>
                  <ConfigCheckbox
                    type="checkbox"
                    checked={visibleColumns[col.key]}
                    onChange={(e) => setVisibleColumns({
                      ...visibleColumns,
                      [col.key]: e.target.checked
                    })}
                  />
                  <ConfigText>
                    <ConfigTitle>{col.label}</ConfigTitle>
                    <ConfigDescription>{col.description}</ConfigDescription>
                  </ConfigText>
                </ConfigItem>
              ))}
            </ConfigGrid>
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={saveColumnConfiguration}
                style={{
                  padding: '0.5rem 1rem',
                  background: theme.colors.primary.main,
                  color: 'white',
                  border: 'none',
                  borderRadius: theme.borderRadius.md,
                  cursor: 'pointer'
                }}
              >
                Save Configuration
              </button>
            </div>
          </ConfigPanel>
        )}

        <TelemetryButtons>
          {[
            { key: 'speed', label: '⚡ Speed', icon: '⚡' },
            { key: 'rpm', label: '🔧 RPM', icon: '🔧' },
            { key: 'temp', label: '🌡️ Temperature', icon: '🌡️' },
            { key: 'fuel', label: '⛽ Fuel Rate', icon: '⛽' },
            { key: 'load', label: '📊 Engine Load', icon: '📊' }
          ].map(view => (
            <TelemetryButton
              key={view.key}
              active={telemetryView === view.key}
              onClick={() => setTelemetryView(view.key)}
            >
              {view.icon} {view.label}
            </TelemetryButton>
          ))}
        </TelemetryButtons>
      </ControlsCard>

      {/* Map */}
      <MapContainer>
        <TripMap 
          track={mapTrack}
          trackPoints={trackPoints}
          waypoints={waypoints}
          startPoint={trackPoints.length > 0 ? {
            lat: parseFloat(trackPoints[0].latitude),
            lon: parseFloat(trackPoints[0].longitude),
            name: 'Start',
            time: trackPoints[0].recorded_at
          } : null}
          endPoint={trackPoints.length > 0 ? {
            lat: parseFloat(trackPoints[trackPoints.length - 1].latitude),
            lon: parseFloat(trackPoints[trackPoints.length - 1].longitude),
            name: 'End',
            time: trackPoints[trackPoints.length - 1].recorded_at
          } : null}
          height="600px"
          showStats={true}
          telemetryView={telemetryView}
          selectedIndex={selectedPoint}
          onPointSelect={handlePointSelect}
          selectedWaypointIndex={selectedWaypointIndex}
          onWaypointSelect={handleWaypointSelect}
          getPointColor={(point) => getTelemetryColor(point, telemetryView)}
          preferenceUnits={prefUnits}
        />
      </MapContainer>

      {/* Track Points Table with Telemetry */}
      <TableCard>
        <TableHeader>
          <TableTitle>Track Points & Telemetry</TableTitle>
        </TableHeader>
        <TableContainer>
          <Table>
            <TableHead>
              <tr>
                <TableHeaderCell>#</TableHeaderCell>
                {visibleColumns.time && (
                  <TableHeaderCell>Time</TableHeaderCell>
                )}
                {visibleColumns.position && (
                  <TableHeaderCell>Position</TableHeaderCell>
                )}
                {visibleColumns.speed && (
                  <TableHeaderCell>Speed ({displaySpeedUnit()})</TableHeaderCell>
                )}
                {visibleColumns.rpm && (
                  <TableHeaderCell>RPM (P/S)</TableHeaderCell>
                )}
                {visibleColumns.temp && (
                  <TableHeaderCell>Temp (P/S) ({displayTemperatureUnit()})</TableHeaderCell>
                )}
                {visibleColumns.load && (
                  <TableHeaderCell>Load (%)</TableHeaderCell>
                )}
                {visibleColumns.fuelRate && (
                  <TableHeaderCell>Fuel Rate ({displayFuelRateUnit()})</TableHeaderCell>
                )}
                {visibleColumns.heading && (
                  <TableHeaderCell>Heading</TableHeaderCell>
                )}
                {visibleColumns.depth && (
                  <TableHeaderCell>Depth (m)</TableHeaderCell>
                )}
              </tr>
            </TableHead>
            <tbody>
              {trackPoints.length === 0 ? (
                <tr>
                  <TableCell
                    colSpan={12}
                    style={{ textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary }}
                  >
                    {loading
                      ? 'Loading track points…'
                      : telemetryLoading
                        ? 'Loading telemetry…'
                        : 'No telemetry rows for this trip. GPS route may still appear on the map above.'}
                  </TableCell>
                </tr>
              ) : null}
              {trackPoints.map((point, index) => {
                const color = getTelemetryColor(point, telemetryView);
                return (
                  <TableRow 
                    key={index}
                    ref={(el) => { rowRefs.current[index] = el; }}
                    selected={selectedPoint === index}
                    even={index % 2 === 0}
                    color={color}
                    onClick={() => handlePointSelect(index === selectedPoint ? null : index)}
                  >
                    <TableCell>{index + 1}</TableCell>
                    {visibleColumns.time && (
                      <TableCell small>
                        {new Date(point.recorded_at).toLocaleTimeString()}
                      </TableCell>
                    )}
                    {visibleColumns.position && (
                      <TableCell small>
                        {parseFloat(point.latitude).toFixed(5)}°, {parseFloat(point.longitude).toFixed(5)}°
                      </TableCell>
                    )}
                    {visibleColumns.speed && (
                      <TableCell>
                        {(() => {
                          const rawKnots = (typeof point.speed_knots === 'number' && !isNaN(point.speed_knots))
                            ? point.speed_knots
                            : toKnotsFromMps(point.speed_mps);
                          return `${displaySpeed(rawKnots).toFixed(1)} ${displaySpeedUnit()}`;
                        })()}
                      </TableCell>
                    )}
                    {visibleColumns.rpm && (
                      <TableCell>
                        {formatPair(point.rpm_port, point.rpm_starboard, (v) => Math.round(v))}
                      </TableCell>
                    )}
                    {visibleColumns.temp && (
                      <TableCell>
                        {formatPair(
                          point.temp_port,
                          point.temp_starboard,
                          (v) => `${displayTemperature(v).toFixed(0)}${displayTemperatureUnit()}`
                        )}
                      </TableCell>
                    )}
                    {visibleColumns.load && (
                      <TableCell>
                        {formatPair(point.load_port, point.load_starboard, (v) => v.toFixed(1))}
                      </TableCell>
                    )}
                    {visibleColumns.fuelRate && (
                      <TableCell>
                        {formatFuelLph(point)}
                      </TableCell>
                    )}
                    {visibleColumns.heading && (
                      <TableCell>
                        {point.heading != null ? `${Number(point.heading).toFixed(0)}°` : '—'}
                      </TableCell>
                    )}
                    {visibleColumns.depth && (
                      <TableCell>
                        {point.depth_meters != null ? Number(point.depth_meters).toFixed(1) : '—'}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </tbody>
          </Table>
        </TableContainer>
      </TableCard>
    </Container>
  );
};

export default TripMapVisualization;

